;(function () {
  const OPEN_CLASS = 'is-open'
  const BODY_OPEN_CLASS = 'pdp-2026-model-modal-open'
  const MODEL_VIEWER_SCRIPT = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js'
  const VIEWER_BG = '#F1F1F1'
  // Built-in model-viewer lighting (no network). Brighter frontal light for product GLBs.
  const BUILTIN_ENVIRONMENT = 'legacy'

  let loaderPromise = null
  const environmentBlobCache = {}
  const environmentFetchInflight = {}
  const HDR_FETCH_TIMEOUT_MS = 10000

  function loadModelViewer() {
    if (window.customElements && window.customElements.get('model-viewer')) {
      return Promise.resolve()
    }

    if (loaderPromise) return loaderPromise

    loaderPromise = new Promise(function (resolve, reject) {
      const $script = document.createElement('script')
      $script.type = 'module'
      $script.src = MODEL_VIEWER_SCRIPT
      $script.addEventListener('load', function () {
        resolve()
      })
      $script.addEventListener('error', reject)
      document.head.appendChild($script)
    })
      .then(function () {
        if (window.customElements) {
          return window.customElements.whenDefined('model-viewer')
        }
      })
      .catch(function (err) {
        loaderPromise = null
        throw err
      })

    return loaderPromise
  }

  function getViewerConfig($modal) {
    const fromWindow = (window.pdpConfig && window.pdpConfig.modelViewer) || window.pdpModelViewerConfig || {}
    const byVariant = (window.pdpConfig && window.pdpConfig.modelViewerByVariant) || window.pdpModelViewerByVariant || {}
    const ds = ($modal && $modal.dataset) || {}
    let fromVariant = null

    // Prefer explicit modal data attributes; otherwise match by media alt on the viewer.
    if (!ds.modelToneMapping) {
      const $viewer = $modal && $modal.querySelector('model-viewer')
      const variantKey = ($viewer && $viewer.getAttribute('alt')) || ''
      if (variantKey) {
        const key = String(variantKey).trim()
        if (byVariant[key]) {
          fromVariant = byVariant[key]
        } else {
          const keyLower = key.toLowerCase()
          for (const mapKey of Object.keys(byVariant)) {
            if (String(mapKey).trim().toLowerCase() === keyLower) {
              fromVariant = byVariant[mapKey]
              break
            }
          }
        }
      }
    }

    const base = Object.assign({}, fromWindow, fromVariant || {})

    return {
      toneMapping: ds.modelToneMapping || base.toneMapping || 'neutral',
      environmentImage:
        ds.modelEnvironmentImage != null && ds.modelEnvironmentImage !== undefined
          ? ds.modelEnvironmentImage
          : base.environmentImage || '',
      exposure: ds.modelExposure || base.exposure || '1.2',
      shadowIntensity: ds.modelShadowIntensity || base.shadowIntensity || '1',
      shadowSoftness: ds.modelShadowSoftness || base.shadowSoftness || '0.5',
      minCameraOrbit: ds.modelMinCameraOrbit || base.minCameraOrbit || 'auto 25deg auto',
      maxCameraOrbit: ds.modelMaxCameraOrbit || base.maxCameraOrbit || 'auto 100deg auto',
      enableAr: ds.modelEnableAr != null ? ds.modelEnableAr === 'true' : base.enableAr !== false,
      arModes: ds.modelArModes || base.arModes || 'webxr scene-viewer quick-look',
    }
  }

  function setAttr($el, attr, val) {
    if (val != null && val !== '') $el.setAttribute(attr, val)
  }

  function resolveEnvironmentImage($viewer, cfg) {
    return (
      (cfg && cfg.environmentImage) ||
      ($viewer && $viewer.dataset && $viewer.dataset.environmentImage) ||
      ''
    )
  }

  function fetchEnvironmentAsBlobUrl(url) {
    if (!url) return Promise.resolve('')
    if (url === BUILTIN_ENVIRONMENT || url === 'neutral') return Promise.resolve(url)
    if (environmentBlobCache[url]) return Promise.resolve(environmentBlobCache[url])
    if (environmentFetchInflight[url]) return environmentFetchInflight[url]

    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    var timeoutId = window.setTimeout(function () {
      if (controller) controller.abort()
    }, HDR_FETCH_TIMEOUT_MS)

    var fetchOpts = { mode: 'cors', credentials: 'omit' }
    if (controller) fetchOpts.signal = controller.signal

    environmentFetchInflight[url] = fetch(url, fetchOpts)
      .then(function (res) {
        if (!res.ok) throw new Error('HDR fetch failed: ' + res.status)
        return res.blob()
      })
      .then(function (blob) {
        if (blob.size < 1024) throw new Error('HDR too small')
        var blobUrl = URL.createObjectURL(blob)
        environmentBlobCache[url] = blobUrl
        return blobUrl
      })
      .finally(function () {
        window.clearTimeout(timeoutId)
        delete environmentFetchInflight[url]
      })

    return environmentFetchInflight[url]
  }

  function setEnvironmentImage($viewer, url) {
    if (!$viewer || !url) return
    if ('environmentImage' in $viewer) {
      $viewer.environmentImage = url
    } else {
      $viewer.setAttribute('environment-image', url)
    }
  }

  function restoreBuiltinLighting($viewer) {
    if (!$viewer) return
    // IMPORTANT: do not set environmentImage = null — that can wipe IBL and
    // leave dark metallic GLBs as pure black silhouettes.
    setEnvironmentImage($viewer, BUILTIN_ENVIRONMENT)
  }

  function applyInteractiveSettings($viewer, cfg) {
    setAttr($viewer, 'tone-mapping', cfg.toneMapping || 'neutral')
    setAttr($viewer, 'shadow-intensity', cfg.shadowIntensity)
    setAttr($viewer, 'shadow-softness', cfg.shadowSoftness)
    setAttr($viewer, 'exposure', cfg.exposure)
    setAttr($viewer, 'min-camera-orbit', cfg.minCameraOrbit)
    setAttr($viewer, 'max-camera-orbit', cfg.maxCameraOrbit)

    // Immediate built-in lighting so the model is never unlit while HDR loads.
    restoreBuiltinLighting($viewer)

    // Defer GLB fetch until dismissPoster() on modal open.
    $viewer.setAttribute('reveal', 'manual')
    $viewer.setAttribute('camera-controls', '')
    $viewer.setAttribute('touch-action', 'none')
    $viewer.removeAttribute('disable-pan')
    $viewer.removeAttribute('disable-zoom')
    $viewer.removeAttribute('disable-tap')
    $viewer.removeAttribute('interaction-prompt')

    if (cfg.enableAr) {
      $viewer.setAttribute('ar', '')
      setAttr($viewer, 'ar-modes', cfg.arModes)
    } else {
      $viewer.removeAttribute('ar')
      $viewer.removeAttribute('ar-modes')
    }

    $viewer.style.backgroundColor = VIEWER_BG
    $viewer.style.setProperty('--poster-color', VIEWER_BG)
    $viewer.style.pointerEvents = 'auto'

    if ('cameraControls' in $viewer) {
      $viewer.cameraControls = true
    }
  }

  function ensureViewerElement($modal) {
    const $content = $modal.querySelector('.pdp-2026-model-modal__content')
    if (!$content) return null

    const cfg = getViewerConfig($modal)
    let $viewer = $content.querySelector('model-viewer')

    if (!$viewer) {
      const glbUrl = $modal.dataset.modelGlb
      if (!glbUrl) return null

      $viewer = document.createElement('model-viewer')
      $viewer.className = 'pdp-2026-model-viewer'
      $viewer.setAttribute('src', glbUrl)
      $viewer.setAttribute('reveal', 'manual')
      if ($modal.dataset.modelPoster) {
        $viewer.setAttribute('poster', $modal.dataset.modelPoster)
      }
      $content.appendChild($viewer)
    }

    if (!$viewer.getAttribute('src') && $modal.dataset.modelGlb) {
      $viewer.setAttribute('src', $modal.dataset.modelGlb)
    }

    if (!$viewer.getAttribute('poster') && $modal.dataset.modelPoster) {
      $viewer.setAttribute('poster', $modal.dataset.modelPoster)
    }

    applyInteractiveSettings($viewer, cfg)
    return $viewer
  }

  function beginManualReveal($viewer) {
    if (!$viewer) return
    if (typeof $viewer.dismissPoster === 'function') {
      try {
        $viewer.dismissPoster()
      } catch (e) {}
    }
  }

  function waitForModel($viewer) {
    if (!$viewer) return Promise.resolve(false)
    if ($viewer.loaded) return Promise.resolve(true)

    return new Promise(function (resolve) {
      var settled = false
      function finish(ok) {
        if (settled) return
        settled = true
        $viewer.removeEventListener('load', onLoad)
        $viewer.removeEventListener('error', onError)
        resolve(ok)
      }
      function onLoad() {
        finish(true)
      }
      function onError() {
        finish(false)
      }
      $viewer.addEventListener('load', onLoad)
      $viewer.addEventListener('error', onError)
      window.setTimeout(function () {
        finish(Boolean($viewer.loaded))
      }, 12000)
    })
  }

  function waitForEnvironment($viewer, timeoutMs) {
    return new Promise(function (resolve) {
      var settled = false
      function finish(ok) {
        if (settled) return
        settled = true
        $viewer.removeEventListener('environment-change', onEnv)
        resolve(ok)
      }
      function onEnv() {
        finish(true)
      }
      $viewer.addEventListener('environment-change', onEnv)
      window.setTimeout(function () {
        finish(false)
      }, timeoutMs || 5000)
    })
  }

  function applyEnvironmentSafely($viewer, preferredUrl) {
    if (!$viewer) return Promise.resolve(false)

    // Always start from built-in lighting (no network dependency).
    restoreBuiltinLighting($viewer)

    if (!preferredUrl || preferredUrl === BUILTIN_ENVIRONMENT) {
      return Promise.resolve(true)
    }

    return fetchEnvironmentAsBlobUrl(preferredUrl)
      .then(function (blobUrl) {
        var envPromise = waitForEnvironment($viewer, 6000)
        setEnvironmentImage($viewer, blobUrl)
        return envPromise.then(function () {
          return true
        })
      })
      .catch(function () {
        restoreBuiltinLighting($viewer)
        return false
      })
  }

  function revealInteractiveViewer($viewer, cfg) {
    if (!$viewer) return Promise.resolve()

    // Idempotent if beginManualReveal already started/dismissed the poster.
    beginManualReveal($viewer)

    if ('cameraControls' in $viewer) {
      $viewer.cameraControls = true
    }
    $viewer.setAttribute('camera-controls', '')
    $viewer.setAttribute('touch-action', 'none')
    $viewer.style.pointerEvents = 'auto'

    if (typeof $viewer.updateFraming === 'function') {
      try {
        $viewer.updateFraming()
      } catch (e) {}
    }
    window.dispatchEvent(new Event('resize'))

    return applyEnvironmentSafely($viewer, resolveEnvironmentImage($viewer, cfg)).then(function () {
      if (cfg.exposure) setAttr($viewer, 'exposure', cfg.exposure)
      if (cfg.toneMapping) setAttr($viewer, 'tone-mapping', cfg.toneMapping)
    })
  }

  function initModelViewer($modal) {
    const $viewer = ensureViewerElement($modal)
    if (!$viewer) return Promise.resolve()
    const cfg = getViewerConfig($modal)

    return loadModelViewer()
      .then(function () {
        // With reveal="manual", dismissPoster starts the GLB fetch.
        beginManualReveal($viewer)
        return waitForModel($viewer)
      })
      .then(function (loaded) {
        if (!loaded && $viewer.getAttribute('src')) {
          var src = $viewer.getAttribute('src')
          $viewer.removeAttribute('src')
          $viewer.setAttribute('src', src)
          beginManualReveal($viewer)
          return waitForModel($viewer)
        }
        return loaded
      })
      .then(function () {
        return revealInteractiveViewer($viewer, cfg)
      })
      .catch(function () {})
  }

  function mountModal($modal) {
    if ($modal.parentElement !== document.body) {
      document.body.appendChild($modal)
    }
  }

  function openModal($modal) {
    mountModal($modal)
    $modal.classList.add(OPEN_CLASS)
    document.body.classList.add(BODY_OPEN_CLASS)

    const $close = $modal.querySelector('.pdp-2026-model-modal__close')
    if ($close) $close.focus()

    window.requestAnimationFrame(function () {
      initModelViewer($modal).catch(function () {})
    })
  }

  function closeModal($modal) {
    $modal.classList.remove(OPEN_CLASS)
  }

  window.Pdp2026ProductModel = {
    open: function (modalId) {
      const $modal = document.querySelector('.pdp-2026-model-modal[data-modal-id="' + modalId + '"]')
      if (!$modal) return
      openModal($modal)
    },

    closeAll: function () {
      document.querySelectorAll('.pdp-2026-model-modal.' + OPEN_CLASS).forEach(closeModal)
      document.body.classList.remove(BODY_OPEN_CLASS)
    },

    loadModelViewer: loadModelViewer,
  }

  if (document.querySelector('.pdp-2026-model-slide__viewer, .pdp-2026-model-modal model-viewer')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        loadModelViewer().catch(function () {})
      })
    } else {
      loadModelViewer().catch(function () {})
    }
  }
})()
